import { Alert, DatePicker, Form, Modal, Select } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs, { type Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../app/api.ts";
import { ApiError, applyFieldErrors } from "../app/apiError.ts";
import { classKeys, formatDate, useActiveTeachers, type ClassSubject } from "../app/classes.ts";

interface Props {
  classId: string;
  subject: ClassSubject | undefined;
  mode: "assign" | "end";
  onClose(): void;
}

/** Field names match the API (teacherId, from, to) so server field errors land under the right input. */
interface AssignForm {
  teacherId?: string;
  from?: Dayjs;
  to?: Dayjs;
}

/**
 * Sets who teaches a subject from a date (the previous teacher's assignment ends the day before and stays in the history),
 * or ends the current assignment on a date. Only active teachers who teach the subject are offered.
 */
export function AssignTeacherModal({ classId, subject, mode, onClose }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<AssignForm>();
  const [summary, setSummary] = useState<string[]>([]);
  const teachers = useActiveTeachers(subject !== undefined && mode === "assign");

  // Each opening starts from a clean form: no teacher left over from the previous dialog, today as the date.
  useEffect(() => {
    if (subject) {
      form.resetFields();
      form.setFieldsValue({ from: dayjs(), to: dayjs() });
      setSummary([]);
    }
  }, [subject, mode, form]);

  const mutation = useMutation({
    mutationFn: async (values: AssignForm) => {
      if (!subject) return;
      const path = { id: classId, subjectId: subject.subjectId };
      const date = (mode === "assign" ? values.from : values.to)?.format("YYYY-MM-DD") ?? null;
      const result =
        mode === "assign"
          ? await api.PUT("/academic/classes/{id}/subjects/{subjectId}/teacher", { params: { path }, body: { teacherId: values.teacherId ?? null, from: date } })
          : await api.POST("/academic/classes/{id}/subjects/{subjectId}/teacher/end", { params: { path }, body: { to: date } });
      if (!result.data) throw new ApiError(result.response.status, result.error);
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: classKeys.detail(classId) }),
        queryClient.invalidateQueries({ queryKey: classKeys.teachers(classId) }),
        queryClient.invalidateQueries({ queryKey: classKeys.all }),
        queryClient.invalidateQueries({ queryKey: ["audit"] }),
      ]);
      setSummary([]);
      onClose();
    },
    onError: (err) => setSummary(applyFieldErrors(form, err, t("app.error"))),
  });

  const options = (teachers.data ?? [])
    .filter((teacher) => subject !== undefined && teacher.subjects.some((s) => s.id === subject.subjectId))
    .map((teacher) => ({ value: teacher.id, label: teacher.fullName }));

  return (
    <Modal
      open={subject !== undefined}
      title={mode === "assign" ? t("classes.assignTitle", { subject: subject?.subjectName ?? "" }) : t("classes.endTitle", { subject: subject?.subjectName ?? "" })}
      okText={t("family.save")}
      cancelText={t("family.cancel")}
      confirmLoading={mutation.isPending}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      {summary.length > 0 && <Alert type="error" showIcon role="alert" style={{ marginBottom: 16 }} title={summary.map((m) => <div key={m}>{m}</div>)} />}
      {mode === "assign" && subject?.teacher && (
        <Alert type="info" showIcon style={{ marginBottom: 16 }} title={t("classes.replaceHint", { name: subject.teacher.fullName, since: formatDate(subject.teacherSince) })} />
      )}
      <Form<AssignForm> form={form} layout="vertical" requiredMark={false} onFinish={(values) => mutation.mutate(values)}>
        {mode === "assign" && (
          <Form.Item
            name="teacherId"
            label={t("classes.teacher")}
            extra={options.length === 0 && !teachers.isPending ? t("classes.noTeacherForSubject") : undefined}
            rules={[{ required: true, message: t("family.validation.required") }]}
          >
            <Select options={options} loading={teachers.isPending} showSearch={{ optionFilterProp: "label" }} />
          </Form.Item>
        )}
        <Form.Item name={mode === "assign" ? "from" : "to"} label={mode === "assign" ? t("classes.from") : t("classes.to")} rules={[{ required: true, message: t("family.validation.required") }]}>
          <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" allowClear={false} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
